<script setup>
import { reactive, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const uiFlags = useMapGetter('multiChannelCampaigns/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');

// Liquid variables available in the message body (rendered per contact).
const LIQUID_VARS = [
  { token: 'contact.first_name', key: 'FIRST_NAME' },
  { token: 'contact.last_name', key: 'LAST_NAME' },
  { token: 'contact.name', key: 'FULL_NAME' },
  { token: 'contact.email', key: 'EMAIL' },
  { token: 'contact.phone_number', key: 'PHONE' },
];

// Weekday bit = JS getDay() index (0=Sun .. 6=Sat).
const WEEKDAYS = [
  { value: 1, key: 'MON' },
  { value: 2, key: 'TUE' },
  { value: 3, key: 'WED' },
  { value: 4, key: 'THU' },
  { value: 5, key: 'FRI' },
  { value: 6, key: 'SAT' },
  { value: 0, key: 'SUN' },
];

const ROUTING_STRATEGIES = [
  { value: 'most_responsive', key: 'MOST_RESPONSIVE' },
  { value: 'priority_order', key: 'PRIORITY' },
  { value: 'broadcast_all', key: 'BROADCAST' },
];

const initialState = {
  title: '',
  message: '',
  allAudience: false,
  includeCold: false,
  selectedAudience: [],
  allChannels: false,
  selectedChannels: [],
  routingStrategy: 'most_responsive',
  selectedDays: [1, 2, 3, 4, 5, 6, 0],
  sendFrom: '',
  sendTo: '',
  sendTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
};

const state = reactive({ ...initialState });
const hasSubmitted = ref(false);

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  routingStrategy: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => uiFlags.value.isCreating);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const audienceList = computed(() => mapToOptions(labels.value, 'id', 'title'));
const inboxOptions = computed(() => mapToOptions(inboxes.value, 'id', 'name'));

const weekdayOptions = computed(() =>
  WEEKDAYS.map(d => ({
    value: d.value,
    label: t(`CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.WEEKDAYS.${d.key}`),
  }))
);

const routingOptions = computed(() =>
  ROUTING_STRATEGIES.map(r => ({
    value: r.value,
    label: t(`CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.ROUTING.OPTIONS.${r.key}`),
  }))
);

const audienceValid = computed(
  () => state.allAudience || state.selectedAudience.length > 0
);
const channelsValid = computed(
  () => state.allChannels || state.selectedChannels.length > 0
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.MULTI_CHANNEL.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  audience:
    hasSubmitted.value && !audienceValid.value
      ? t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.ERROR')
      : '',
  channels:
    hasSubmitted.value && !channelsValid.value
      ? t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.CHANNELS.ERROR')
      : '',
}));

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !audienceValid.value || !channelsValid.value
);

const insertVariable = token => {
  const snippet = `{{ ${token} }}`;
  state.message =
    state.message && !state.message.endsWith(' ')
      ? `${state.message} ${snippet}`
      : `${state.message}${snippet}`;
};

const onVariableDragStart = (token, event) => {
  event.dataTransfer?.setData('text/plain', `{{ ${token} }}`);
};

const resetState = () => {
  Object.assign(state, initialState);
  hasSubmitted.value = false;
};
const handleCancel = () => emit('cancel');

const daysToBitmask = days =>
  (days ?? []).reduce((mask, day) => mask | (1 << day), 0) || 127;

const prepareCampaignDetails = () => ({
  title: state.title,
  default_message: state.message,
  audience: state.allAudience
    ? [{ type: 'All' }]
    : state.selectedAudience.map(id => ({ id, type: 'Label' })),
  include_cold_contacts: state.includeCold,
  inbox_ids: state.allChannels
    ? inboxOptions.value.map(o => o.value)
    : state.selectedChannels,
  routing_strategy: state.routingStrategy,
  send_days: daysToBitmask(state.selectedDays),
  send_from: state.sendFrom || null,
  send_to: state.sendTo || null,
  send_timezone: state.sendTimezone || 'UTC',
});

const handleSubmit = async () => {
  hasSubmitted.value = true;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !audienceValid.value || !channelsValid.value) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-2">
      <TextArea
        v-model="state.message"
        :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.MESSAGE.LABEL')"
        :placeholder="
          t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.MESSAGE.PLACEHOLDER')
        "
        :max-length="4096"
        show-character-count
        :message="formErrors.message"
        :message-type="formErrors.message ? 'error' : 'info'"
      />
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.VARIABLES.LABEL') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="variable in LIQUID_VARS"
            :key="variable.token"
            type="button"
            draggable="true"
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors border rounded-lg cursor-grab select-none border-n-blue-6 bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 hover:border-n-blue-8 active:cursor-grabbing"
            :title="`{{ ${variable.token} }}`"
            @click="insertVariable(variable.token)"
            @dragstart="onVariableDragStart(variable.token, $event)"
          >
            <span class="text-sm leading-none text-n-blue-10">+</span>
            {{
              t(`CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.VARIABLES.${variable.key}`)
            }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.LABEL') }}
      </span>
      <label
        class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11"
      >
        <Checkbox v-model="state.allAudience" />
        {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.ALL') }}
      </label>
      <TagMultiSelectComboBox
        v-if="!state.allAudience"
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="
          t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.PLACEHOLDER')
        "
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <label
        class="flex items-start gap-2 text-sm cursor-pointer text-n-slate-11"
      >
        <Checkbox v-model="state.includeCold" class="mt-0.5" />
        <span class="flex flex-col gap-0.5">
          {{
            t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.INCLUDE_COLD.LABEL')
          }}
          <span class="text-xs text-n-slate-10">
            {{
              t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.AUDIENCE.INCLUDE_COLD.HINT')
            }}
          </span>
        </span>
      </label>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.CHANNELS.LABEL') }}
      </span>
      <label
        class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-11"
      >
        <Checkbox v-model="state.allChannels" />
        {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.CHANNELS.ALL') }}
      </label>
      <TagMultiSelectComboBox
        v-if="!state.allChannels"
        v-model="state.selectedChannels"
        :options="inboxOptions"
        :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.CHANNELS.LABEL')"
        :placeholder="
          t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.CHANNELS.PLACEHOLDER')
        "
        :has-error="!!formErrors.channels"
        :message="formErrors.channels"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label for="routing" class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.ROUTING.LABEL') }}
      </label>
      <ComboBox
        id="routing"
        v-model="state.routingStrategy"
        :options="routingOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div
      class="flex flex-col gap-3 p-3 border rounded-xl border-n-weak bg-n-alpha-1"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.LABEL') }}
      </span>
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.DAYS') }}
        </span>
        <TagMultiSelectComboBox
          v-model="state.selectedDays"
          :options="weekdayOptions"
          :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.DAYS')"
          :placeholder="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.DAYS')"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>
      <div class="flex items-end gap-3">
        <div class="flex flex-col flex-1 gap-1.5">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.FROM') }}
          </span>
          <Input v-model="state.sendFrom" type="time" />
        </div>
        <div class="flex flex-col flex-1 gap-1.5">
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.TO') }}
          </span>
          <Input v-model="state.sendTo" type="time" />
        </div>
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.SCHEDULE.TIMEZONE') }}
        </span>
        <Input v-model="state.sendTimezone" />
      </div>
    </div>

    <div class="flex items-center justify-between w-full gap-3 pt-1">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.MULTI_CHANNEL.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
